import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import IconSvg from 'src/assets/icons/IconSvg';
import Button from 'src/components/button/Button';
import ToggleButton from 'src/components/button/ToggleButton';
import RunesSelectionInput from 'src/components/input/RunesSelectionInput';
import SearchCollectionInput from 'src/components/input/SearchCollectionInput';
import { appConfig } from 'src/config/appConfig';
import { useViewport } from 'src/context/ViewportContext';
import ChoseOriginalPrizeModal from 'src/components/modal/ChoseOriginalPrizeModal';
import RaffleSuccessModal from 'src/components/modal/RaffleSuccessModal';
import SelectWalletModal from 'src/components/modal/SelectWalletModal';
import { OrdinalToken } from 'src/hooks/useOrdinals';
import { RuneToken, useRunes } from 'src/hooks/useRunes';
import { useWallet } from 'src/hooks/useWallet';

const CreateRaffle = () => {
  const navigate = useNavigate();
  const [isFeatured, setIsFeatured] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [selectedOrdinal, setSelectedOrdinal] = useState<OrdinalToken | null>(null);

  // Runes state
  const [selectedRune, setSelectedRune] = useState<RuneToken | null>(null);
  const [runeAmount, setRuneAmount] = useState('');

  // Form fields
  const [endDate, setEndDate] = useState('');
  const [supply, setSupply] = useState('');
  const [ticketPrice, setTicketPrice] = useState('');
  const [ticketLimitPerWallet, setTicketLimitPerWallet] = useState('');
  const [numberOfWinners, setNumberOfWinners] = useState('');
  const [marketPrice, setMarketPrice] = useState(0);
  const [selectedTimeOffset, setSelectedTimeOffset] = useState<number | null>(null);
  const [selectedMpPct, setSelectedMpPct] = useState<number | null>(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [transactionLink, setTransactionLink] = useState<string | undefined>();

  const { isMobile } = useViewport();
  const { isConnected, ordinalsAddress, paymentAddress, providerId, signMessage } = useWallet();
  const walletAddress = ordinalsAddress?.address || paymentAddress?.address;
  const { tokens: runeTokens, isLoading: isRunesLoading } = useRunes(isConnected ? walletAddress : undefined);

  // Derived prize type
  const runeAmountNum = parseFloat(runeAmount) || 0;
  const isRunePrize = !!(selectedRune && runeAmountNum > 0);
  const isOrdinalPrize = !!selectedOrdinal;
  const hasPrize = isOrdinalPrize || isRunePrize;

  // Auto-calculate TTV = supply * ticketPrice
  const supplyNum = parseInt(supply, 10) || 0;
  const ticketPriceNum = parseFloat(ticketPrice) || 0;
  const ttv = supplyNum * ticketPriceNum;

  // The "reference price" - floor price for ordinals, market price for runes
  const referencePrice = isRunePrize ? selectedRune!.marketPriceBtc : marketPrice;
  const priceLabel = isRunePrize ? 'MP' : 'FP';

  // Auto-fill recommended defaults
  const autoFillDefaults = () => {
    if (!endDate) {
      const d = new Date();
      d.setHours(d.getHours() + 24);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      setEndDate(`${dd}/${mm}/${yyyy}`);
      setSelectedTimeOffset(24);
    }
    if (!supply) setSupply('100');
    if (!ticketPrice) setTicketPrice('0.0001');
    if (!numberOfWinners) setNumberOfWinners('1');
  };

  const handleSelectOrdinal = (token: OrdinalToken) => {
    setSelectedOrdinal(token);
    // Clear rune selection (mutual exclusion)
    setSelectedRune(null);
    setRuneAmount('');
    // Set floor price for ordinals
    setMarketPrice(0.012);
    autoFillDefaults();
  };

  const handleClearOrdinal = () => {
    setSelectedOrdinal(null);
    setMarketPrice(0);
  };

  const handleSelectRune = (rune: RuneToken) => {
    setSelectedRune(rune);
    // Clear ordinal selection (mutual exclusion)
    setSelectedOrdinal(null);
    setMarketPrice(0);
  };

  const handleRuneAmountChange = (value: string) => {
    setRuneAmount(value);
    // When user enters a rune amount, clear ordinal and auto-fill
    if (selectedRune && parseFloat(value) > 0) {
      setSelectedOrdinal(null);
      autoFillDefaults();
    }
  };

  const handleRuneAmountPercent = (pct: number) => {
    if (!selectedRune) return;
    const amount = Math.floor(selectedRune.balance * pct / 100);
    setRuneAmount(String(amount));
    if (amount > 0) {
      setSelectedOrdinal(null);
      autoFillDefaults();
    }
  };

  // Set ticket price so that TTV = referencePrice * (1 + pct)
  const setTtvFromPrice = (pct: number) => {
    if (referencePrice <= 0 || supplyNum <= 0) return;
    const targetTtv = referencePrice * (1 + pct / 100);
    const newTicketPrice = targetTtv / supplyNum;
    setTicketPrice(newTicketPrice.toFixed(8).replace(/0+$/, '').replace(/\.$/, ''));
    setSelectedMpPct(pct);
  };

  // TTV vs reference price percentage
  const ttvRefPct = referencePrice > 0 && ttv > 0
    ? Math.round(((ttv - referencePrice) / referencePrice) * 100)
    : null;

  const setEndDateOffset = (hours: number) => {
    const d = new Date();
    d.setHours(d.getHours() + hours);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    setEndDate(`${dd}/${mm}/${yyyy}`);
    setSelectedTimeOffset(hours);
  };

  // Parse dd/mm/yyyy to ISO date string
  const parseEndDate = (dateStr: string): string => {
    const [dd, mm, yyyy] = dateStr.split('/');
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd)).toISOString();
  };

  const handleGoLive = async () => {
    if (!walletAddress || !providerId) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // 1. Sign message with wallet (mock transaction)
      const signature = await signMessage(
        `Confirm creation of raffle for ${prizeName} on Omega One.`,
      );
      if (!signature) {
        setIsSubmitting(false);
        return;
      }

      // 2. Ensure user exists in DB
      const userRes = await axios.post(`${appConfig.serviceUrl}/users/ensure`, {
        walletId: providerId,
        walletAddress,
      });
      const userId: string = userRes.data.id;

      // 3. Build raffle payload
      const payload: Record<string, any> = {
        userId,
        prizeType: isRunePrize ? 'RUNES' : 'ORDINALS',
        endDate: parseEndDate(endDate),
        supply: parseInt(supply, 10),
        ticketPriceSats: Math.round(parseFloat(ticketPrice) * 1e8),
        isFeatured,
        numberOfWinners: parseInt(numberOfWinners, 10) || 1,
      };

      // Ordinal-specific fields
      if (isOrdinalPrize && selectedOrdinal) {
        payload.ordinalId = selectedOrdinal.id;
        payload.ordinalName = selectedOrdinal.meta?.name || '';
        payload.ordinalImageUrl = selectedOrdinal.contentPreviewURI || '';
        payload.ordinalCollectionName = selectedOrdinal.collection?.name || '';
        payload.ordinalInscriptionNumber = String(selectedOrdinal.inscriptionNumber);
        payload.ordinalContentType = selectedOrdinal.contentType || '';
      }

      // Rune-specific fields
      if (isRunePrize && selectedRune) {
        payload.runesTokenName = selectedRune.name;
        payload.runesAmount = parseFloat(runeAmount);
        payload.runesImageUrl = selectedRune.imageUrl || '';
      }

      // Optional advanced settings
      const ticketLimitNum = parseInt(ticketLimitPerWallet, 10);
      if (ticketLimitNum > 0) {
        payload.ticketLimitPerWallet = ticketLimitNum;
      }

      // 4. Create raffle (DRAFT)
      const raffleRes = await axios.post(`${appConfig.serviceUrl}/raffles`, payload);
      const raffleId: string = raffleRes.data.id;

      // 5. Go live
      const goLiveRes = await axios.patch(`${appConfig.serviceUrl}/raffles/${raffleId}/go-live`, { userId });

      // Success — show congratulations modal
      setTransactionLink(goLiveRes.data?.transactionLink);
      setIsSuccessModalOpen(true);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to create raffle';
      setSubmitError(Array.isArray(msg) ? msg.join(', ') : msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Display helpers
  const prizeName = isRunePrize
    ? `${runeAmount} ${selectedRune!.name}`
    : selectedOrdinal?.meta?.name || 'No prize selected';

  const prizeHeaderName = isRunePrize
    ? selectedRune!.name
    : selectedOrdinal?.collection?.name || 'Select a prize';

  const prizeHeaderImage = isRunePrize
    ? selectedRune!.imageUrl
    : selectedOrdinal?.collection?.imageURI || '';

  return (
    <>
      <ChoseOriginalPrizeModal
        isOpen={isOpen}
        close={() => setIsOpen(false)}
        onSelect={handleSelectOrdinal}
      />

      <SelectWalletModal
        isOpen={isWalletModalOpen}
        close={() => setIsWalletModalOpen(false)}
      />

      <RaffleSuccessModal
        isOpen={isSuccessModalOpen}
        transactionLink={transactionLink}
      />

      <section>
        <h3
          className={`mb-6 xl:mb-8 text-brand-magnolia ${
            showSummary && isMobile && 'xl:text-[32px] text-[22px]'
          }`}
        >
          <span>Create Raffle </span>

          {showSummary && <span className="text-grayscale-500">/ Summary</span>}
        </h3>

        <div className="flex flex-col gap-6 xl:gap-7 xl:flex-row">
          {!showSummary && (
            <div className="flex gap-2 items-center px-4 py-2 w-full rounded-lg border bg-grayscale-800 border-grayscale-700 xl:hidden">
              <IconSvg icon="reportM" className="flex-shrink-0 text-grayscale-400" />

              <div className="text-sm text-grayscale-400">
                Upload your prize to get recommended settings, which you can review and adjust to
                fit your needs.
              </div>
            </div>
          )}

          {/* LEFT PANEL */}
          <div className="xl:max-w-[698px] w-full h-fit overflow-hidden">
            {!isConnected ? (
              <div className="relative w-full h-auto rounded-lg bg-grayscale-900">
                <div className="w-full aspect-square flex flex-col gap-6 items-center justify-center">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setIsWalletModalOpen(true)}
                  >
                    Connect Wallet
                  </Button>

                  <div className="text-grayscale-400 text-sm">
                    Connect your wallet to choose a prize.
                  </div>
                </div>
              </div>
            ) : showSummary ? (
              /* Summary left panel: show prize image with overlay */
              <div className="relative w-full h-auto rounded-lg bg-grayscale-900">
                {isRunePrize ? (
                  <>
                    <img
                      src={selectedRune!.imageUrl}
                      alt={selectedRune!.name}
                      className="object-cover w-full aspect-square rounded-lg"
                    />
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="backdrop-blur-md bg-[#1B1B1FAD] rounded-lg px-6 py-3 text-center">
                        <h4 className="text-brand-magnolia text-[22px] font-semibold truncate">
                          {runeAmount} {selectedRune!.name}
                        </h4>
                      </div>
                    </div>
                  </>
                ) : selectedOrdinal ? (
                  <>
                    {selectedOrdinal.contentPreviewURI ? (
                      <img
                        src={selectedOrdinal.contentPreviewURI}
                        alt={selectedOrdinal.meta?.name || ''}
                        className="object-cover w-full aspect-square rounded-lg"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-grayscale-800 rounded-lg flex items-center justify-center">
                        <IconSvg icon="infoM" className="text-grayscale-500 w-16 h-16" />
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            ) : selectedOrdinal ? (
              <div className="relative w-full h-auto rounded-lg bg-grayscale-900 group">
                {selectedOrdinal.contentPreviewURI ? (
                  <img
                    src={selectedOrdinal.contentPreviewURI}
                    alt={selectedOrdinal.meta?.name || ''}
                    className="object-cover w-full h-full rounded-lg"
                  />
                ) : (
                  <div className="w-full aspect-square bg-grayscale-800 rounded-lg flex items-center justify-center">
                    <IconSvg icon="infoM" className="text-grayscale-500 w-16 h-16" />
                  </div>
                )}

                <div className="absolute inset-0 bg-[#1B1B1FCC] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg">
                  <div className="flex gap-4">
                    <button
                      className="flex flex-col gap-2 items-center"
                      onClick={() => setIsOpen(true)}
                    >
                      <IconSvg
                        icon="editM"
                        className="text-brand-tangerine w-12 h-12"
                      />
                      <div className="text-brand-magnolia text-sm font-medium">Change</div>
                    </button>

                    <button
                      className="flex flex-col gap-2 items-center"
                      onClick={handleClearOrdinal}
                    >
                      <IconSvg
                        icon="closeM"
                        className="text-system-error w-12 h-12"
                      />
                      <div className="text-brand-magnolia text-sm font-medium">Remove</div>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative w-full h-auto rounded-lg bg-grayscale-900">
                <div className="w-full aspect-square flex items-center justify-center">
                  <button
                    className="flex flex-col gap-6 items-center"
                    onClick={() => setIsOpen(true)}
                  >
                    <IconSvg
                      icon="plusM"
                      className="text-brand-tangerine xl:w-[148px] xl:h-[148px] w-[92px] h-[92px]"
                    />

                    <div className="text-brand-magnolia xl:text-[24px] text-lg font-medium">
                      Add an Ordinals Prize
                    </div>
                  </button>
                </div>
              </div>
            )}

            {!showSummary && isConnected && (
              <>
                <div className="xl:text-[24px] text-lg text-grayscale-300 text-center xl:my-4 my-2 font-medium">
                  Or
                </div>

                <div className="px-3 py-5 mb-2 rounded-lg xl:mb-4 xl:px-6 xl:py-8 bg-grayscale-900">
                  <div className="xl:!text-[24px] text-lg font-medium text-brand-magnolia text-center tracking-[-0.5px] mb-6">
                    Add a Runes Prize
                  </div>

                  <div className="grid grid-cols-1 gap-y-2 gap-x-3 mb-2 xl:grid-cols-2">
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        placeholder="Enter Amount"
                        value={runeAmount}
                        onChange={(e) => handleRuneAmountChange(e.target.value)}
                        disabled={runeTokens.length === 0}
                        className="p-3 pl-[25px] w-full rounded-lg outline-none bg-brand-black placeholder:text-grayscale-500 text-brand-magnolia h-[48px] border border-transparent disabled:opacity-40"
                      />

                      <button
                        onClick={() => handleRuneAmountPercent(50)}
                        disabled={!selectedRune}
                        className="bg-brand-black text-grayscale-400 h-[48px] w-[54px] rounded-md border border-brand-granite flex items-center justify-center flex-shrink-0 text-sm disabled:opacity-40"
                      >
                        50%
                      </button>

                      <button
                        onClick={() => handleRuneAmountPercent(100)}
                        disabled={!selectedRune}
                        className="bg-brand-black text-grayscale-400 h-[48px] w-[54px] rounded-md border border-brand-granite flex items-center justify-center flex-shrink-0 text-sm disabled:opacity-40"
                      >
                        MAX
                      </button>
                    </div>

                    <RunesSelectionInput
                      runes={runeTokens}
                      selected={selectedRune}
                      onSelect={handleSelectRune}
                      isLoading={isRunesLoading}
                    />
                  </div>

                  <div className="text-sm text-grayscale-200">
                    Your balance: {selectedRune ? selectedRune.balance.toLocaleString() : '--'}
                  </div>
                </div>

                <div className="flex gap-2 items-center xl:justify-center">
                  <IconSvg icon="plusS" className="text-brand-tangerine" />

                  <div className="underline text-grayscale-200">
                    Contact us to have another collection verified on Omega
                  </div>
                </div>
              </>
            )}
          </div>

          {/* RIGHT PANEL */}
          <div className="pt-3 w-full border-t xl:border-0 border-grayscale-600 xl:pt-0">
            {!showSummary && (
              <div className="hidden gap-2 items-center px-4 py-2 mb-6 w-full rounded-lg border xl:flex bg-grayscale-800 border-grayscale-700">
                <IconSvg icon="infoM" className="text-grayscale-400" />

                <div className="text-sm text-grayscale-400">
                  Upload your prize to get recommended settings, which you can review and adjust to
                  fit your needs.
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-between items-end">
              <div className="space-y-1 min-w-0 flex-1">
                {!isConnected ? (
                  <>
                    <h3 className="text-brand-magnolia leading-[43px] xl:text-[32px] text-[22px]">
                      Raffle Name
                    </h3>
                    <div className="text-sm text-grayscale-400">
                      The raffle will be named after the Prize you choose
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex gap-4 items-center">
                      {prizeHeaderImage ? (
                        <img
                          src={prizeHeaderImage}
                          alt={prizeHeaderName}
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-grayscale-600 rounded-full flex-shrink-0"></div>
                      )}

                      <div className="flex gap-1 items-center text-base xl:text-lg text-brand-magnolia min-w-0">
                        <div className="truncate">
                          {prizeHeaderName}
                        </div>
                        {(isRunePrize || selectedOrdinal?.collection) && (
                          <IconSvg icon="verifiedS" className="text-brand-tangerine flex-shrink-0" />
                        )}
                      </div>
                    </div>

                    <h3 className="text-brand-magnolia leading-[43px] xl:text-[32px] text-[22px] truncate">
                      {prizeName}
                    </h3>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 sm:w-[384px] w-full flex-shrink-0">
                {isRunePrize ? (
                  <>
                    <div className="px-4 py-2 space-y-1 rounded-lg bg-grayscale-800">
                      <div className="text-xss text-grayscale-400">Price (sats/N)</div>

                      <div className="flex gap-2 items-center">
                        <span className="text-lg font-medium text-brand-magnolia">
                          {selectedRune!.marketPriceBtc}
                        </span>
                      </div>

                      <div className="text-xss text-grayscale-400">≈ $--</div>
                    </div>

                    <div className="px-4 py-2 space-y-1 rounded-lg bg-grayscale-800">
                      <div className="text-xss text-grayscale-400">Volume (1d)</div>

                      <div className="flex gap-2 items-center">
                        <IconSvg icon="bitcoinS" />

                        <span className="text-lg font-medium text-brand-magnolia">
                          {selectedRune!.volume1dBtc}
                        </span>
                      </div>

                      <div className="text-xss text-grayscale-400">≈ $--</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-4 py-2 space-y-1 rounded-lg bg-grayscale-800">
                      <div className="text-xss text-grayscale-400">Floor Price</div>

                      <div className="flex gap-2 items-center">
                        <IconSvg icon="bitcoinS" />

                        <span className="text-lg font-medium text-brand-magnolia">
                          {marketPrice > 0 ? marketPrice : '--'}
                        </span>
                      </div>

                      <div className="text-xss text-grayscale-400">≈ $--</div>
                    </div>

                    <div className="px-4 py-2 space-y-1 rounded-lg bg-grayscale-800">
                      <div className="text-xss text-grayscale-400">Top Properties</div>

                      <div className="flex gap-2 items-center">
                        <IconSvg icon="bitcoinS" />

                        <span className="text-lg font-medium text-brand-magnolia">--</span>
                      </div>

                      <div className="text-xss text-grayscale-400">≈ $--</div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="my-3 w-full border-t xl:my-6 border-grayscale-600"></div>

            {!showSummary && (
              <>
                <div className="grid grid-cols-1 gap-y-3 gap-x-6 mb-3 xl:mb-6 3xl:grid-cols-2">
                  <div className="px-3 py-5 space-y-3 w-full rounded-lg xl:space-y-6 xl:px-6 xl:py-6 bg-grayscale-800">
                    <div className="space-y-2">
                      <label htmlFor="raffle-end-date" className="text-lg text-brand-magnolia">
                        Raffle End Date
                      </label>

                      <div className="flex gap-2 items-center">
                        <input
                          type="text"
                          name="raffle-end-date"
                          id="raffle-end-date"
                          placeholder="dd/mm/yyyy"
                          pattern="\d{2}/\d{2}/\d{4}"
                          value={endDate}
                          onChange={(e) => { setEndDate(e.target.value); setSelectedTimeOffset(null); }}
                          className="p-3 w-full rounded-lg outline-none bg-grayscale-900 placeholder:text-grayscale-500 text-brand-magnolia h-[48px] border border-transparent"
                        />

                        {[24, 36, 48].map((h) => (
                          <button
                            key={h}
                            type="button"
                            onClick={() => setEndDateOffset(h)}
                            className={`h-[48px] w-[48px] xl:w-[68px] rounded-md border flex items-center justify-center flex-shrink-0 xl:text-base text-sm transition-colors ${
                              selectedTimeOffset === h
                                ? 'bg-grayscale-900 border-brand-tangerine text-brand-magnolia'
                                : 'bg-grayscale-900 border-brand-granite text-grayscale-400'
                            }`}
                          >
                            {h}h
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="raffle-supply" className="flex justify-between items-end">
                        <span className="text-lg text-brand-magnolia">Supply</span>
                        <span className="text-sm text-grayscale-400">Min: 2 / Max: 10.000</span>
                      </label>

                      <input
                        type="text"
                        name="raffle-supply"
                        id="raffle-supply"
                        placeholder="e.g. 100"
                        value={supply}
                        onChange={(e) => setSupply(e.target.value)}
                        className="p-3 w-full rounded-lg outline-none bg-grayscale-900 placeholder:text-grayscale-500 text-brand-magnolia h-[48px] border border-transparent"
                      />
                    </div>

                    <div className="flex gap-4 items-center">
                      <ToggleButton enabled={isFeatured} setEnabled={setIsFeatured} />

                      <div className="flex flex-wrap gap-y-1 gap-x-2 items-center">
                        <div className="text-lg text-brand-magnolia">
                          🔥 Feature Raffle on Main Page
                        </div>

                        <span className="text-xs text-grayscale-400 tracking-[-0.5px]">
                          (+1% of TTV)
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="px-3 py-5 space-y-6 w-full rounded-lg xl:px-6 xl:py-6 bg-grayscale-800">
                    <div className="space-y-2">
                      <label htmlFor="raffle-ticket-price" className="flex justify-between items-end">
                        <span className="text-lg text-brand-magnolia">Ticket Price</span>
                        <span className="text-sm text-grayscale-400">≈ $</span>
                      </label>

                      <div className="relative">
                        <input
                          type="text"
                          name="raffle-ticket-price"
                          id="raffle-ticket-price"
                          placeholder="0.0001"
                          value={ticketPrice}
                          onChange={(e) => { setTicketPrice(e.target.value); setSelectedMpPct(null); }}
                          className="p-3 pl-[25px] w-full rounded-lg outline-none bg-grayscale-900 placeholder:text-grayscale-500 text-brand-magnolia h-[48px] border border-transparent"
                        />

                        <IconSvg
                          icon="bitcoinS"
                          className="absolute left-3 top-1/2 -translate-y-1/2"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="raffle-ttv" className="flex justify-between items-end">
                        <span className="text-lg text-brand-magnolia">
                          {isRunePrize ? 'Total Ticket Value' : 'TTV'}
                        </span>
                        <span className="text-sm text-grayscale-400">≈ $</span>
                      </label>

                      <div className="flex gap-2 items-center">
                        <div className="relative w-full">
                          <input
                            type="text"
                            name="raffle-ttv"
                            id="raffle-ttv"
                            readOnly
                            value={ttv > 0 ? ttv.toFixed(4) : ''}
                            placeholder="Auto-calculated"
                            className="p-3 pl-[25px] w-full rounded-lg outline-none bg-grayscale-900 placeholder:text-grayscale-500 text-brand-magnolia h-[48px] border border-transparent cursor-not-allowed"
                          />

                          <IconSvg
                            icon="bitcoinS"
                            className="absolute left-3 top-1/2 -translate-y-1/2"
                          />
                        </div>

                        {[5, 10, 15].map((pct) => (
                          <button
                            key={pct}
                            type="button"
                            onClick={() => setTtvFromPrice(pct)}
                            disabled={referencePrice <= 0 || supplyNum <= 0}
                            className={`h-[48px] w-[56px] xl:w-[68px] rounded-md border flex items-center justify-center flex-shrink-0 text-xs xl:text-sm disabled:opacity-40 transition-colors ${
                              selectedMpPct === pct
                                ? 'bg-grayscale-900 border-brand-tangerine text-brand-magnolia'
                                : 'bg-grayscale-900 border-brand-granite text-grayscale-400'
                            }`}
                          >
                            {priceLabel} +{pct}%
                          </button>
                        ))}
                      </div>

                      <div className="text-xs text-grayscale-400">
                        {ttvRefPct !== null
                          ? `The TTV of this raffle is ${ttvRefPct}% ${ttvRefPct >= 0 ? 'over' : 'under'} the current collection ${priceLabel}`
                          : `Select a prize to see TTV relative to ${isRunePrize ? 'market price' : 'floor price'}`}
                      </div>
                    </div>
                  </div>
                </div>

                <Disclosure as="div" className="w-full rounded-lg bg-grayscale-800 !p-6">
                  <DisclosureButton className="flex justify-between items-center w-full group">
                    <div className="!text-[24px] font-medium text-brand-magnolia">
                      Advanced Settings
                    </div>

                    <IconSvg
                      icon="arrowDownM"
                      className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
                    />
                  </DisclosureButton>

                  <DisclosurePanel className="!mt-6 text-grayscale-400 transition-all duration-200">
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <div className="text-lg text-brand-magnolia">Holder Only Mode</div>

                        <SearchCollectionInput className="w-full" />

                        <button className="flex gap-1 items-center text-sm text-grayscale-300">
                          <IconSvg icon="plusS" />
                          <span>add another collection</span>
                        </button>
                      </div>

                      <div className="flex flex-col gap-6 w-full sm:flex-row">
                        <div className="space-y-2 w-full">
                          <label className="text-lg text-brand-magnolia">
                            Ticket Limit per Wallet
                          </label>

                          <input
                            type="text"
                            placeholder="No Limit"
                            value={ticketLimitPerWallet}
                            onChange={(e) => setTicketLimitPerWallet(e.target.value)}
                            className="p-3 w-full rounded-lg outline-none bg-grayscale-900 placeholder:text-grayscale-500 text-brand-magnolia h-[48px] border border-transparent"
                          />

                          <div className="text-xs text-grayscale-400">
                            Users can purchase 40% of total tickets as standard
                          </div>
                        </div>

                        <div className="space-y-2 w-full">
                          <label className="flex justify-between items-end">
                            <span className="text-lg text-brand-magnolia">Number of Winners</span>
                            <span className="text-sm text-grayscale-400">Max: 10</span>
                          </label>

                          <input
                            type="text"
                            placeholder="Max 10"
                            value={numberOfWinners}
                            onChange={(e) => setNumberOfWinners(e.target.value)}
                            className="p-3 w-full rounded-lg outline-none bg-grayscale-900 placeholder:text-grayscale-500 text-brand-magnolia h-[48px] border border-transparent"
                          />
                        </div>
                      </div>
                    </div>
                  </DisclosurePanel>
                </Disclosure>
              </>
            )}

            {showSummary && (
              <>
                <div className="mb-3 space-y-3 xl:mb-6 xl:px-6">
                  <div className="flex justify-between items-center xl:text-lg">
                    <div className="text-grayscale-400">Prize</div>
                    <div className="text-brand-magnolia truncate ml-4 text-right">
                      {prizeName}
                    </div>
                  </div>

                  <div className="flex justify-between items-center xl:text-lg">
                    <div className="text-grayscale-400">Supply</div>
                    <div className="text-brand-magnolia">{supply ? Number(supply).toLocaleString() : '--'}</div>
                  </div>

                  <div className="flex justify-between items-center xl:text-lg">
                    <div className="text-grayscale-400">Ticket Price</div>

                    <div className="flex gap-2 items-center text-grayscale-400">
                      <IconSvg icon="bitcoinS" />
                      <div className="text-brand-magnolia">{ticketPrice || '--'}</div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center xl:text-lg">
                    <div className="text-grayscale-400">Total Ticket Value (TTV)</div>

                    <div className="flex gap-2 items-center text-grayscale-400">
                      <IconSvg icon="bitcoinS" />
                      <div className="text-brand-magnolia">{ttv > 0 ? ttv.toFixed(4) : '--'}</div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center xl:text-lg">
                    <div className="text-grayscale-400">Duration</div>
                    <div className="text-brand-magnolia">
                      {selectedTimeOffset ? `${selectedTimeOffset}h` : endDate || '--'}
                    </div>
                  </div>

                  <div className="flex justify-between items-center xl:text-lg">
                    <div className="text-grayscale-400">Ticket Limit per Wallet</div>
                    <div className="text-brand-magnolia">{ticketLimitPerWallet || 'No Limit'}</div>
                  </div>

                  <div className="flex justify-between items-center xl:text-lg">
                    <div className="text-grayscale-400">Number of Winners</div>
                    <div className="text-brand-magnolia">{numberOfWinners || '1'}</div>
                  </div>
                </div>

                <div className="p-6 rounded-lg bg-grayscale-800">
                  <div className="flex gap-4 items-center">
                    <ToggleButton enabled={isFeatured} setEnabled={setIsFeatured} />

                    <div className="flex flex-wrap gap-y-1 gap-x-2 items-center">
                      <div className="text-lg text-brand-magnolia">
                        🔥 Feature Raffle on Main Page
                      </div>

                      <span className="text-xs text-grayscale-400 tracking-[-0.5px]">
                        (+1% of TTV)
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="my-3 w-full border-t xl:my-6 border-grayscale-600"></div>

            {!showSummary && (
              <div className="flex flex-col gap-4 justify-end items-center sm:flex-row">
                <div className="flex gap-3 items-center">
                  <ToggleButton enabled={agreedToTerms} setEnabled={setAgreedToTerms} />

                  <div className="text-grayscale-200">
                    I agree to the{' '}
                    <a href="#" className="underline">
                      Terms and Conditions
                    </a>
                  </div>
                </div>

                <Button
                  size={isMobile ? 'xs' : 'sm'}
                  disabled={!agreedToTerms || !hasPrize}
                  onClick={() => setShowSummary(true)}
                  className="w-full sm:w-fit"
                >
                  Next
                </Button>
              </div>
            )}

            {showSummary && (
              <div className="space-y-3">
                {submitError && (
                  <div className="px-4 py-3 rounded-lg bg-system-error/10 text-system-error text-sm">
                    {submitError}
                  </div>
                )}

                <div className="flex gap-4 justify-between items-center">
                  <button
                    className="flex gap-2 items-center px-4 py-4 text-lg font-semibold leading-5 text-grayscale-200"
                    onClick={() => setShowSummary(false)}
                    disabled={isSubmitting}
                  >
                    <IconSvg icon="arrowLeftS" />

                    <span>Go Back</span>
                  </button>

                  <Button size="sm" onClick={handleGoLive} disabled={isSubmitting}>
                    {isSubmitting ? 'Publishing...' : 'Go Live'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default CreateRaffle;
